import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { OfflineRegion, BoundingBox } from '@/types';
import { tileService } from '@/services/tileService';

interface OfflineState {
  // Regions
  regions: OfflineRegion[];
  downloadQueue: string[];
  currentDownloadId: string | null;
  downloadProgress: number;

  // Storage info
  usedStorage: number;
  availableStorage: number;

  // Network status
  isOnline: boolean;
  useOfflineWhenAvailable: boolean;

  // Actions
  addRegion: (region: Omit<OfflineRegion, 'id' | 'downloadedAt' | 'expiresAt' | 'status' | 'progress'>) => string;
  removeRegion: (id: string) => void;
  updateRegionStatus: (id: string, status: OfflineRegion['status'], progress?: number) => void;
  updateRegionMeta: (id: string, meta: { sizeBytes?: number; tileCount?: number }) => void;
  refreshStorageInfo: () => Promise<void>;
  normalizeStaleDownloads: () => void;
  startDownload: (id: string) => void;
  cancelDownload: (id: string) => void;
  setDownloadProgress: (progress: number) => void;
  setStorageInfo: (used: number, available: number) => void;
  setIsOnline: (online: boolean) => void;
  setUseOfflineWhenAvailable: (use: boolean) => void;
  getRegionForLocation: (lng: number, lat: number) => OfflineRegion | null;
  isLocationOfflineAvailable: (lng: number, lat: number, zoom: number) => boolean;
}

export const useOfflineStore = create<OfflineState>()(
  persist(
    (set, get) => ({
      // Initial state
      regions: [],
      downloadQueue: [],
      currentDownloadId: null,
      downloadProgress: 0,
      usedStorage: 0,
      availableStorage: 0,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      useOfflineWhenAvailable: true,

      // Actions
      addRegion: (regionData) => {
        const id = `region-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const region: OfflineRegion = {
          ...regionData,
          id,
          downloadedAt: new Date(),
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
          status: 'pending',
          progress: 0,
        };
        set((state) => ({
          regions: [...state.regions, region],
          downloadQueue: [...state.downloadQueue, id],
        }));
        return id;
      },

      removeRegion: (id) => set((state) => ({
        regions: state.regions.filter((r) => r.id !== id),
        downloadQueue: state.downloadQueue.filter((qid) => qid !== id),
        currentDownloadId: state.currentDownloadId === id ? null : state.currentDownloadId,
      })),

      updateRegionStatus: (id, status, progress) => set((state) => ({
        regions: state.regions.map((r) =>
          r.id === id
            ? {
                ...r,
                status,
                progress: progress ?? r.progress,
                downloadedAt: status === 'complete' ? new Date() : r.downloadedAt,
              }
            : r
        ),
      })),

      // Record the *actual* downloaded footprint (bytes + tiles stored) so the
      // saved region reflects reality instead of the pre-download estimate.
      updateRegionMeta: (id, meta) => set((state) => ({
        regions: state.regions.map((r) =>
          r.id === id
            ? {
                ...r,
                sizeBytes: meta.sizeBytes ?? r.sizeBytes,
                tileCount: meta.tileCount ?? r.tileCount,
              }
            : r
        ),
      })),

      // Pull live IndexedDB usage/quota from the storage engine so the storage
      // bar stays accurate after downloads and deletes reclaim space.
      refreshStorageInfo: async () => {
        try {
          const { used, available } = await tileService.getStorageInfo();
          set({ usedStorage: used, availableStorage: available });
        } catch {
          // storage.estimate() unavailable — leave prior values untouched.
        }
      },

      // A download interrupted by a page reload leaves a region stuck in
      // 'downloading'. Demote such regions to 'error' so the user must
      // deliberately retry (avoids surprise data use on metered connections).
      normalizeStaleDownloads: () => set((state) => ({
        currentDownloadId: null,
        downloadQueue: [],
        downloadProgress: 0,
        regions: state.regions.map((r) =>
          r.status === 'downloading' || r.status === 'pending'
            ? { ...r, status: 'error' as const, progress: 0 }
            : r
        ),
      })),

      startDownload: (id) => {
        const { regions, downloadQueue, currentDownloadId } = get();
        if (currentDownloadId) {
          // Add to queue if something is already downloading
          if (!downloadQueue.includes(id)) {
            set((state) => ({
              downloadQueue: [...state.downloadQueue, id],
            }));
          }
          return;
        }

        const region = regions.find((r) => r.id === id);
        if (!region) return;

        set({
          currentDownloadId: id,
          downloadProgress: 0,
        });

        // Update status to downloading
        set((state) => ({
          regions: state.regions.map((r) =>
            r.id === id ? { ...r, status: 'downloading', progress: 0 } : r
          ),
        }));
      },

      cancelDownload: (id) => set((state) => {
        const wasCurrent = state.currentDownloadId === id;
        const newQueue = state.downloadQueue.filter((qid) => qid !== id);
        // If we cancelled the active download, promote the next queued region;
        // otherwise the active download is untouched.
        const nextId = wasCurrent ? newQueue[0] ?? null : state.currentDownloadId;

        return {
          downloadQueue: newQueue,
          currentDownloadId: nextId,
          // Reset the global progress only when the active download changed.
          downloadProgress: wasCurrent ? 0 : state.downloadProgress,
          regions: state.regions.map((r) => {
            // Cancelled region -> back to pending, cleared.
            if (r.id === id) return { ...r, status: 'pending', progress: 0 };
            // Freshly promoted region -> start it clean, so the global
            // downloadProgress (now 0) can't be smeared onto stale progress.
            if (wasCurrent && r.id === nextId) {
              return { ...r, status: 'downloading', progress: 0 };
            }
            return r;
          }),
        };
      }),

      setDownloadProgress: (progress) => set((state) => ({
        downloadProgress: progress,
        regions: state.regions.map((r) =>
          r.id === state.currentDownloadId ? { ...r, progress } : r
        ),
      })),

      setStorageInfo: (used, available) => set({
        usedStorage: used,
        availableStorage: available,
      }),

      setIsOnline: (online) => set({ isOnline: online }),

      setUseOfflineWhenAvailable: (use) => set({ useOfflineWhenAvailable: use }),

      getRegionForLocation: (lng, lat) => {
        const { regions } = get();
        return regions.find((r) =>
          r.status === 'complete' &&
          isPointInBounds({ lng, lat }, r.bounds)
        ) ?? null;
      },

      isLocationOfflineAvailable: (lng, lat, zoom) => {
        const { regions } = get();
        return regions.some((r) =>
          r.status === 'complete' &&
          isPointInBounds({ lng, lat }, r.bounds) &&
          zoom >= r.minZoom &&
          zoom <= r.maxZoom
        );
      },
    }),
    {
      name: 'tensormaps-offline-store',
      partialize: (state) => ({
        regions: state.regions,
        useOfflineWhenAvailable: state.useOfflineWhenAvailable,
      }),
    }
  )
);

function isPointInBounds(
  point: { lng: number; lat: number },
  bounds: BoundingBox
): boolean {
  return (
    point.lng >= bounds.sw.lng &&
    point.lng <= bounds.ne.lng &&
    point.lat >= bounds.sw.lat &&
    point.lat <= bounds.ne.lat
  );
}
